import React, { useState, useEffect, useRef } from 'react';
import styles from './NetworkStatusPopup.module.css';

const NetworkStatusPopup = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [networkStatus, setNetworkStatus] = useState({
    online: true,
    connectionType: 'unknown',
    effectiveType: '4g',
    rtt: 0,
    downlink: 0,
    wasRecentlyOffline: false
  });
  
  const previousOnlineStatus = useRef(true);
  const wasOfflineRef = useRef(false);
  const showBackOnlineRef = useRef(false);
  const backOnlineTimerRef = useRef(null);
  const hasShownSlowConnectionRef = useRef(false);

  useEffect(() => {
    // Check if we've already shown slow connection warning this session
    const hasShownSlow = sessionStorage.getItem('slowConnectionShown');
    if (hasShownSlow === 'true') {
      hasShownSlowConnectionRef.current = true;
    }

    const updateNetworkStatus = () => {
      const online = navigator.onLine;
      const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      
      // Check if we just came back online
      if (!previousOnlineStatus.current && online) {
        wasOfflineRef.current = true;
        showBackOnlineRef.current = true;
        
        // Clear any existing timer
        if (backOnlineTimerRef.current) {
          clearTimeout(backOnlineTimerRef.current);
        }
        
        // Set timer to hide back online notification after 3 seconds
        backOnlineTimerRef.current = setTimeout(() => {
          showBackOnlineRef.current = false;
          setIsVisible(false);
        }, 3000);
      }
      
      // Update previous status
      previousOnlineStatus.current = online;
      
      if (connection) {
        setNetworkStatus(prev => ({
          ...prev,
          online,
          connectionType: connection.type || 'unknown',
          effectiveType: connection.effectiveType || '4g',
          rtt: connection.rtt || 0,
          downlink: connection.downlink || 0,
          wasRecentlyOffline: wasOfflineRef.current && online
        }));
      } else {
        setNetworkStatus(prev => ({
          ...prev,
          online,
          wasRecentlyOffline: wasOfflineRef.current && online
        }));
      }
      
      // Determine if we should show the popup
      if (showBackOnlineRef.current) {
        setIsVisible(true);
        return;
      }
      
      if (!online) {
        setIsVisible(true);
        wasOfflineRef.current = true;
      } else {
        const downlink = connection?.downlink || 0;
        const rtt = connection?.rtt || 0;
        const effectiveType = connection?.effectiveType || '4g';
        
        // Check for slow connection - only if speed < 0.5 Mbps AND we haven't shown it this session
        const isSlowConnection = downlink < 0.5 && !hasShownSlowConnectionRef.current;
        
        if (isSlowConnection) {
          setIsVisible(true);
          // Mark that we've shown slow connection warning
          hasShownSlowConnectionRef.current = true;
          sessionStorage.setItem('slowConnectionShown', 'true');
        } else if (effectiveType === 'slow-2g' && !hasShownSlowConnectionRef.current) {
          // Also check for slow-2g specifically
          setIsVisible(true);
          hasShownSlowConnectionRef.current = true;
          sessionStorage.setItem('slowConnectionShown', 'true');
        } else {
          // Only hide if we're not showing back online notification
          if (!showBackOnlineRef.current) {
            setIsVisible(false);
          }
        }
      }
    };

    // Initial check with a small delay to get accurate connection data
    setTimeout(updateNetworkStatus, 1000);

    // Add event listeners
    window.addEventListener('online', updateNetworkStatus);
    window.addEventListener('offline', updateNetworkStatus);
    
    if (navigator.connection) {
      navigator.connection.addEventListener('change', updateNetworkStatus);
    }

    // Auto-hide after 8 seconds if it's a slow connection warning
    const autoHideTimer = setInterval(() => {
      if (showBackOnlineRef.current) return;
      
      const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      const downlink = connection?.downlink || 0;
      
      // Only auto-hide slow connection warnings (not offline warnings)
      if (networkStatus.online && downlink < 0.5 && hasShownSlowConnectionRef.current) {
        setIsVisible(false);
      }
    }, 8000);

    return () => {
      window.removeEventListener('online', updateNetworkStatus);
      window.removeEventListener('offline', updateNetworkStatus);
      if (navigator.connection) {
        navigator.connection.removeEventListener('change', updateNetworkStatus);
      }
      clearInterval(autoHideTimer);
      if (backOnlineTimerRef.current) {
        clearTimeout(backOnlineTimerRef.current);
      }
    };
  }, []);

  const getStatusMessage = () => {
    // Check for back online status first
    if (showBackOnlineRef.current && networkStatus.online) {
      return {
        title: "Back Online!",
        message: "Your internet connection has been restored. You're back online!",
        icon: "✓",
        severity: "backOnline",
        showDetails: false
      };
    }

    if (!networkStatus.online) {
      return {
        title: "Oops, you're offline!",
        message: "It seems you've lost your internet connection. Please check your network settings or try reconnecting.",
        icon: "📶",
        severity: "offline",
        showDetails: true
      };
    }

    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    const downlink = connection?.downlink || 0;
    const effectiveType = connection?.effectiveType || '4g';
    
    if (downlink < 0.5 || effectiveType === 'slow-2g') {
      return {
        title: "Slow Connection Detected",
        message: "Your internet connection is slower than usual. Some features may not work properly. Consider switching to a faster network.",
        icon: "🐌",
        severity: "slow",
        showDetails: true
      };
    }

    return null;
  };

  const handleClose = () => {
    showBackOnlineRef.current = false;
    setIsVisible(false);
    
    if (backOnlineTimerRef.current) {
      clearTimeout(backOnlineTimerRef.current);
      backOnlineTimerRef.current = null;
    }
  };

  const handleDontShowAgain = () => {
    // Store in sessionStorage that user doesn't want to see slow connection warnings
    sessionStorage.setItem('slowConnectionShown', 'true');
    setIsVisible(false);
  };

  const statusInfo = getStatusMessage();

  if (!isVisible || !statusInfo) return null;

  return (
    <div className={`${styles.popupOverlay} ${styles[`${statusInfo.severity}Overlay`]}`}>
      <div className={`${styles.popupContainer} ${styles[statusInfo.severity]}`}>
        <div className={styles.popupHeader}>
          <div className={styles.iconContainer}>
            <span className={styles.icon}>{statusInfo.icon}</span>
          </div>
          <h2 className={styles.title}>{statusInfo.title}</h2>
        </div>
        
        <div className={styles.popupBody}>
          <p className={styles.message}>{statusInfo.message}</p>
          
          {statusInfo.showDetails && (
            <div className={styles.connectionDetails}>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Status:</span>
                <span className={styles.detailValue}>
                  {networkStatus.online ? 'Online' : 'Offline'}
                </span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Connection Type:</span>
                <span className={styles.detailValue}>
                  {networkStatus.connectionType.charAt(0).toUpperCase() + networkStatus.connectionType.slice(1)}
                </span>
              </div>
              {networkStatus.online && (
                <>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Speed:</span>
                    <span className={styles.detailValue}>
                      {networkStatus.effectiveType.toUpperCase()}
                    </span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Download Speed:</span>
                    <span className={styles.detailValue}>
                      {networkStatus.downlink < 0.5 ? '< 0.5' : networkStatus.downlink.toFixed(1)} Mbps
                    </span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Latency:</span>
                    <span className={styles.detailValue}>
                      {networkStatus.rtt}ms
                    </span>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
        
        <div className={styles.popupFooter}>
          {statusInfo.severity === 'backOnline' ? (
            <button 
              onClick={handleClose}
              className={`${styles.actionButton} ${styles.backOnlineButton}`}
            >
              Awesome!
            </button>
          ) : statusInfo.severity === 'slow' ? (
            <>
              <button 
                onClick={handleClose}
                className={`${styles.actionButton} ${styles[`${statusInfo.severity}Button`]}`}
              >
                Got It
              </button>
              <button 
                onClick={handleDontShowAgain}
                className={styles.secondaryButton}
              >
                Don't Show Again
              </button>
            </>
          ) : (
            <>
              <button 
                onClick={handleClose}
                className={`${styles.actionButton} ${styles[`${statusInfo.severity}Button`]}`}
              >
                {statusInfo.severity === 'offline' ? 'Try Again' : 'Got It'}
              </button>
              {statusInfo.severity === 'offline' && (
                <button 
                  onClick={() => window.location.reload()}
                  className={styles.secondaryButton}
                >
                  Refresh Page
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default NetworkStatusPopup;